import { PlusIcon } from 'lucide-react';

import { useEffect, useId, useRef, useState } from 'react';

import { PlaceholderMemberService } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import { Button, Input } from '@splitfiesta/ui';

import { MemberActionRow } from '@/components/members/MemberActionRow';

import { getErrorMessage, handleError } from '@/utils/errorHandler';

export interface SaveSpotFieldProps {
  /** Rejects on failure; the field then keeps the name and shows the error. */
  onSave: (name: string) => Promise<void>;
  /** False hides the closed control; an open field stays to show its error. */
  available: boolean;
  disabled?: boolean;
  testId: string;
}

const ROOT = '-mx-6 border-y border-gray-100 dark:border-gray-800';

/**
 * "Save a spot" at the end of an expense picker: the row turns into a name
 * field in place. No nested <form>: it sits inside the expense form, so
 * Enter is handled here instead of submitting the expense.
 */
export function SaveSpotField({
  onSave,
  available,
  disabled = false,
  testId,
}: SaveSpotFieldProps) {
  const { t } = useTranslation();
  useNamespace('groups');
  const baseId = useId();
  const hintId = `${baseId}-hint`;
  const inputId = `${baseId}-name`;
  const errorId = `${baseId}-error`;
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const controlRef = useRef<HTMLButtonElement>(null);
  const refocusControlRef = useRef(false);

  useEffect(() => {
    if (!editing && refocusControlRef.current) {
      refocusControlRef.current = false;
      controlRef.current?.focus();
    }
  }, [editing]);

  const { valid, trimmed } = PlaceholderMemberService.validateName(name);

  const close = () => {
    refocusControlRef.current = true;
    setEditing(false);
    setName('');
    setError('');
  };

  const submit = async () => {
    if (busy) return;
    if (!trimmed) {
      close();
      return;
    }
    if (!valid) return;
    setBusy(true);
    setError('');
    try {
      await onSave(trimmed);
      close();
    } catch (err: unknown) {
      setError(getErrorMessage(handleError(err, 'groups'), t));
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    if (!available) return null;
    return (
      <div className={ROOT}>
        <MemberActionRow
          ref={controlRef}
          icon={PlusIcon}
          label={t('groups:placeholderMembers.addTitle')}
          onClick={() => setEditing(true)}
          disabled={disabled}
          describedBy={hintId}
          testId={testId}
        />
        <span id={hintId} className="sr-only">
          {t('expenses:saveSpot.a11yHint')}
        </span>
      </div>
    );
  }

  return (
    <div
      className={`${ROOT} space-y-2 px-6 py-3`}
      data-testid={`${testId}-editor`}
    >
      <Input
        id={inputId}
        label={t('groups:placeholderMembers.addTitle')}
        placeholder={t('groups:placeholderMembers.namePlaceholder')}
        value={name}
        onChange={event => {
          setName(event.target.value);
          if (error) setError('');
        }}
        onKeyDown={event => {
          if (event.key === 'Enter') {
            event.preventDefault();
            void submit();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            if (!busy) close();
          }
        }}
        maxLength={50}
        autoComplete="off"
        autoFocus
        readOnly={busy}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        data-testid={`${testId}-input`}
      />
      {error && (
        <p
          id={errorId}
          role="alert"
          className="text-sm text-error-600 dark:text-error-400"
          data-testid={`${testId}-error`}
        >
          {error}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => void submit()}
          disabled={!valid || busy}
          data-testid={`${testId}-save`}
        >
          {busy ? t('common:actions.saving') : t('common:actions.save')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={close}
          disabled={busy}
          data-testid={`${testId}-cancel`}
        >
          {t('common:actions.cancel')}
        </Button>
      </div>
    </div>
  );
}

export default SaveSpotField;
